'use client'

import { useSyncExternalStore } from 'react'

import { normalizeChainedHashFragment } from '@/_lib/next-link-href'

/** Fragment without `#`, decoded once for comparison with `location.hash`. */
export function normalizeRouteHashFragment(raw: string): string {
	if (!raw) return ''
	try {
		return decodeURIComponent(raw)
	} catch {
		return raw
	}
}

export function readHeaderRouteHash(): string {
	if (typeof window === 'undefined') return ''
	const raw = normalizeChainedHashFragment(window.location.hash.replace(/^#/, ''))
	return normalizeRouteHashFragment(raw)
}

let historyPatchInstalled = false
const historyPatchListeners = new Set<() => void>()

function notifyHistoryPatchListeners() {
	historyPatchListeners.forEach(fn => {
		fn()
	})
}

/** Next.js client navigations update the URL via `history.pushState`, which does not fire `hashchange`. */
function ensureHistoryPatchForHashSync() {
	if (typeof window === 'undefined' || historyPatchInstalled) return
	historyPatchInstalled = true
	const { pushState, replaceState } = window.history
	window.history.pushState = function (
		this: History,
		...args: Parameters<typeof pushState>
	) {
		const result = pushState.apply(this, args)
		queueMicrotask(notifyHistoryPatchListeners)
		return result
	}
	window.history.replaceState = function (
		this: History,
		...args: Parameters<typeof replaceState>
	) {
		const result = replaceState.apply(this, args)
		queueMicrotask(notifyHistoryPatchListeners)
		return result
	}
}

function subscribeRouteHash(onChange: () => void) {
	if (typeof window === 'undefined') return () => {}
	ensureHistoryPatchForHashSync()
	historyPatchListeners.add(onChange)
	window.addEventListener('hashchange', onChange)
	window.addEventListener('popstate', onChange)
	return () => {
		historyPatchListeners.delete(onChange)
		window.removeEventListener('hashchange', onChange)
		window.removeEventListener('popstate', onChange)
	}
}

function getRouteHashSnapshot(): string {
	return readHeaderRouteHash()
}

function getRouteHashServerSnapshot(): string {
	return ''
}

/**
 * Location hash for nav active states (pathname omits the fragment in Next.js).
 * Updates on `hashchange`, `popstate`, and History API updates (App Router client navigations).
 */
export function useHeaderRouteHash(): string {
	return useSyncExternalStore(
		subscribeRouteHash,
		getRouteHashSnapshot,
		getRouteHashServerSnapshot
	)
}
